/**
 * Owner: Masa Bayu
 * File: create-demo-catalog.ts
 * Date:Time: 2026-09-15 21:56
 * Description: T2 demo catalog — enriched for discovery affinity / related books.
 */

import {
  bindQxreadPublicationToQxp,
  createDraftCatalogPublication,
  createQxreadCatalogEntry,
  createQxreadPreviewBoundary,
  createQxreadPublicationMetadata,
  createQxreadVersionBinding,
  serializeCatalogEntry,
  transitionListingState,
  type QxreadCatalogSerialized,
} from '@qx/qxread-core';

function buildDemoEntry(input: {
  readonly publicationId: string;
  readonly title: string;
  readonly qxpPublicationRef: string;
  readonly qxpVersionRef: string;
  readonly versionLabel: string;
  readonly authors: readonly string[];
  readonly tags: readonly string[];
  readonly language?: string;
  readonly description?: string;
}): QxreadCatalogSerialized {
  const draft = createDraftCatalogPublication({ title: input.title, visibility: 'public' });
  const bound = bindQxreadPublicationToQxp(
    {
      ...draft.ref,
      publicationId: input.publicationId,
    },
    {
      qxpPublicationRef: input.qxpPublicationRef,
      listedVersion: input.versionLabel,
    },
  );
  const live = transitionListingState(
    {
      ...draft.lifecycle,
      publicationId: input.publicationId,
    },
    'live',
  );
  const metadata = createQxreadPublicationMetadata({
    publicationId: input.publicationId,
    title: input.title,
    description: input.description ?? null,
    authors: input.authors,
    tags: input.tags,
    language: input.language ?? 'en',
  });
  const version = createQxreadVersionBinding({
    publicationId: input.publicationId,
    qxpVersionRef: input.qxpVersionRef,
    versionLabel: input.versionLabel,
    integrityHash: `hash_${input.qxpVersionRef}`,
  });
  const preview = createQxreadPreviewBoundary({
    publicationId: input.publicationId,
    scope: 'sample',
  });
  return serializeCatalogEntry(
    createQxreadCatalogEntry({
      ref: bound,
      lifecycle: live,
      metadata,
      version,
      preview,
    }),
  );
}

const DEMO_CATALOG: readonly QxreadCatalogSerialized[] = Object.freeze([
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_river',
    title: 'River Atlas',
    qxpPublicationRef: 'qxp_pub_demo_river',
    qxpVersionRef: 'qxp_ver_demo_river',
    versionLabel: '1.0.0',
    authors: ['Ada River'],
    tags: ['reference', 'maps'],
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_harbor',
    title: 'Harbor Notes',
    qxpPublicationRef: 'qxp_pub_demo_harbor',
    qxpVersionRef: 'qxp_ver_demo_harbor',
    versionLabel: '1.1.0',
    authors: ['Lin Harbor'],
    tags: ['essays'],
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_delta',
    title: 'Delta Charts',
    qxpPublicationRef: 'qxp_pub_demo_delta',
    qxpVersionRef: 'qxp_ver_demo_delta',
    versionLabel: '1.2.0',
    authors: ['Ada River'],
    tags: ['maps', 'navigation'],
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_quay',
    title: 'Quay Essays',
    qxpPublicationRef: 'qxp_pub_demo_quay',
    qxpVersionRef: 'qxp_ver_demo_quay',
    versionLabel: '2.0.0',
    authors: ['Lin Harbor'],
    tags: ['essays', 'reference'],
  }),
  // Marketplace shelf — original fictional Malay titles (copyright-safe).
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_impian',
    title: 'Jalan Menuju Impian',
    description: 'Kisah seorang pemuda yang mendaki semula hidupnya selepas kegagalan besar.',
    qxpPublicationRef: 'qxp_pub_demo_impian',
    qxpVersionRef: 'qxp_ver_demo_impian',
    versionLabel: '1.0.0',
    authors: ['Azman Idris'],
    tags: ['fiction'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_legasi',
    title: 'Legasi Sang Penakluk',
    description: 'Fantasi epik tentang pewaris takhta yang terpaksa menempuh jalan pulang sendiri.',
    qxpPublicationRef: 'qxp_pub_demo_legasi',
    qxpVersionRef: 'qxp_ver_demo_legasi',
    versionLabel: '1.0.0',
    authors: ['Aisyah Zahra'],
    tags: ['fiction'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_pulau',
    title: 'Pulau di Hujung Senja',
    description:
      'Roman picu air mata tentang pulau kecil, surat-surat lama, dan janji yang tertunda.',
    qxpPublicationRef: 'qxp_pub_demo_pulau',
    qxpVersionRef: 'qxp_ver_demo_pulau',
    versionLabel: '1.0.0',
    authors: ['Nadia Hartawan'],
    tags: ['fiction'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_bahagia',
    title: 'Bahagia Itu Pilihan',
    description: 'Panduan ringkas melatih fikiran melihat kecukupan dalam kehidupan biasa.',
    qxpPublicationRef: 'qxp_pub_demo_bahagia',
    qxpVersionRef: 'qxp_ver_demo_bahagia',
    versionLabel: '1.0.0',
    authors: ['Nora Shahirah'],
    tags: ['self-development'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_emosi',
    title: 'Kecerdasan Emosi',
    description: 'Kunci kehidupan bahagia — mengenali, menerima, dan mengurus emosi harian.',
    qxpPublicationRef: 'qxp_pub_demo_emosi',
    qxpVersionRef: 'qxp_ver_demo_emosi',
    versionLabel: '1.0.0',
    authors: ['Dr. Faridah Ahmad'],
    tags: ['self-development'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_sains',
    title: 'Asas Sains Untuk Semua',
    description: 'Sains asas dijelaskan tanpa jargon — sesuai untuk semua peringkat umur.',
    qxpPublicationRef: 'qxp_pub_demo_sains',
    qxpVersionRef: 'qxp_ver_demo_sains',
    versionLabel: '1.0.0',
    authors: ['Cikgu Hafiz Maulana'],
    tags: ['education'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_belajar',
    title: 'Kaedah Belajar Cepat',
    description: 'Teknik pengulangan aktif dan nota visual untuk pembelajaran yang melekat lama.',
    qxpPublicationRef: 'qxp_pub_demo_belajar',
    qxpVersionRef: 'qxp_ver_demo_belajar',
    versionLabel: '1.0.0',
    authors: ['Dr. Suraya Ismail'],
    tags: ['education'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_bisnes',
    title: 'Bisnes Online Dari Nol',
    description: 'Daripada nol ke untung — langkah praktikal membina kedai digital pertama anda.',
    qxpPublicationRef: 'qxp_pub_demo_bisnes',
    qxpVersionRef: 'qxp_ver_demo_bisnes',
    versionLabel: '1.0.0',
    authors: ['Hafizul Rahman'],
    tags: ['business', 'finance'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_sihat',
    title: 'Gaya Hidup Sihat',
    description: 'Tubuh sihat, minda kuat — tabaran mudah untuk rutin harian yang berkekalan.',
    qxpPublicationRef: 'qxp_pub_demo_sihat',
    qxpVersionRef: 'qxp_ver_demo_sihat',
    versionLabel: '1.0.0',
    authors: ['Dr. Zulkarnain Hassan'],
    tags: ['medicine'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_ai',
    title: 'AI dan Masa Depan Manusia',
    description: 'Pembelajaran mesin, etika, dan masa depan pekerjaan — dalam bahasa terbuka.',
    qxpPublicationRef: 'qxp_pub_demo_ai',
    qxpVersionRef: 'qxp_ver_demo_ai',
    versionLabel: '1.0.0',
    authors: ['Prof. Madya Dr. Rashid Wajdi'],
    tags: ['technology'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_detik',
    title: 'Detik Hening',
    description: 'Refleksi pendek untuk tenang — momen hening yang mengubah hari anda.',
    qxpPublicationRef: 'qxp_pub_demo_detik',
    qxpVersionRef: 'qxp_ver_demo_detik',
    versionLabel: '1.0.0',
    authors: ['Ustazah Mariam Salleh'],
    tags: ['religion'],
    language: 'ms',
  }),
  buildDemoEntry({
    publicationId: 'qxrdpub_demo_kancil',
    title: 'Si Kancil dan Rakan-Rakan',
    description:
      'Kisah penuh nilai si kancil dan rakan-rakan rimba — untuk kanak-kanak dan remaja.',
    qxpPublicationRef: 'qxp_pub_demo_kancil',
    qxpVersionRef: 'qxp_ver_demo_kancil',
    versionLabel: '1.0.0',
    authors: ['Halimah Roslan'],
    tags: ['children-ya'],
    language: 'ms',
  }),
]);

/** Stable demo catalog for T1/T2 UI — not QXP truth. */
export function createDemoCatalog(): readonly QxreadCatalogSerialized[] {
  return DEMO_CATALOG;
}

export function findDemoCatalogEntry(publicationId: string): QxreadCatalogSerialized | null {
  for (const entry of DEMO_CATALOG) {
    if (entry.publicationId === publicationId) {
      return entry;
    }
  }
  return null;
}
